/*
  Glimmery motion (v2)
  - Section entrances: elements with [data-g-reveal] that start below the first screen fade/slide in
    (12–20 px, ~420 ms, small stagger via [data-g-reveal-index] or automatic order inside [data-g-reveal-group]).
    Anything already on screen at load (hero, first buy box) is never hidden. No JS / reduced motion /
    motion level "off" → everything stays visible.
  - Marquee: <g-marquee> with a pause/play button; static under reduced motion.
  - Theme editor: re-initialises on shopify:section:load without duplicate observers.
*/
(() => {
  if (window.GlimmeryMotion) return;

  const root = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionLevel = () => document.body?.dataset.gMotion || 'standard';
  const motionAllowed = () => !reduceQuery.matches && motionLevel() !== 'off' && !window.Shopify?.designMode;

  const seen = new WeakSet();
  let observer = null;

  function revealOne(el) {
    el.classList.add('g-reveal--in');
    el.classList.remove('g-reveal--pending');
    observer?.unobserve(el);
  }

  // A group (e.g. a horizontal swipe row on phones) appears as a whole once its first item is in view, so items
  // that sit off-screen to the side are never left hidden until they are swiped in.
  function reveal(el) {
    const group = el.closest('[data-g-reveal-group]');
    if (!group) return revealOne(el);
    group.querySelectorAll('[data-g-reveal].g-reveal--pending').forEach(revealOne);
    revealOne(el);
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return observer;
  }

  function indexGroups(scope) {
    scope.querySelectorAll('[data-g-reveal-group]').forEach((group) => {
      let i = 0;
      group.querySelectorAll('[data-g-reveal]').forEach((el) => {
        if (!el.hasAttribute('data-g-reveal-index')) el.style.setProperty('--g-reveal-index', String(Math.min(i++, 6)));
      });
    });
    scope.querySelectorAll('[data-g-reveal-index]').forEach((el) => {
      el.style.setProperty('--g-reveal-index', el.getAttribute('data-g-reveal-index'));
    });
  }

  function initReveal(scope = document) {
    const items = Array.from(scope.querySelectorAll('[data-g-reveal]')).filter((el) => !seen.has(el));
    if (!items.length) return;
    items.forEach((el) => seen.add(el));

    if (!motionAllowed() || !getObserver()) {
      items.forEach((el) => el.classList.remove('g-reveal--pending'));
      return;
    }

    indexGroups(scope);
    const fold = window.innerHeight * 0.92;
    items.forEach((el) => {
      const rect = el.getBoundingClientRect();
      // Only hide what the visitor cannot see yet; never delay visible content.
      if (rect.top > fold) {
        el.classList.add('g-reveal--pending');
        observer.observe(el);
      }
    });
  }

  /* ---------- Marquee ---------- */
  class GMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-g-marquee-track]');
      this.button = this.querySelector('[data-g-marquee-toggle]');
      if (!this.track) return;
      // Clone the content once for a seamless loop (hidden from assistive tech). A moved element (theme editor
      // reorder) reconnects with its clone already in place, so only the listener is bound again.
      if (!this.dataset.ready) {
        this.dataset.ready = 'true';
        const clone = this.track.firstElementChild?.cloneNode(true);
        if (clone) {
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.track.appendChild(clone);
        }
        if (!motionAllowed()) this.setPaused(true, true);
      }
      this.onToggle = this.onToggle || (() => this.setPaused(!this.classList.contains('is-paused')));
      this.button?.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onToggle);
    }

    setPaused(paused, auto = false) {
      this.classList.toggle('is-paused', paused);
      if (!this.button) return;
      this.button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      if (auto && reduceQuery.matches) this.button.hidden = true;
    }
  }
  if (!customElements.get('g-marquee')) customElements.define('g-marquee', GMarquee);

  /* ---------- Boot ---------- */
  function boot() {
    initReveal(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  // Keyboard users may tab into content that has not been revealed yet: show it immediately.
  document.addEventListener('focusin', (event) => {
    const pending = event.target.closest?.('.g-reveal--pending');
    if (pending) reveal(pending);
  });
  reduceQuery.addEventListener?.('change', () => {
    document.querySelectorAll('[data-g-reveal].g-reveal--pending').forEach(reveal);
  });

  root.classList.add('g-motion-ready');
  window.GlimmeryMotion = { initReveal, reveal };
})();
